import * as Sentry from "@sentry/react";

const DSN = "https://06c47ef8b6d6fd6d354ab5afa0c5a2a6@o4511388131852288.ingest.de.sentry.io/4511388156887120";

// Hosts where we never want to ship telemetry (sandbox / preview iframe).
const isMonitoredHost = () => {
  if (typeof window === "undefined") return false;
  const h = window.location.hostname;
  return (
    h === "tirevault.se" ||
    h === "www.tirevault.se" ||
    h === "tirevault.app" ||
    h === "www.tirevault.app" ||
    h === "tirevault.lovable.app"
  );
};

// ---------------------------------------------------------------------------
// PII Scrubber
// ---------------------------------------------------------------------------
// Covers:
// - e-mail addresses (any language)
// - Swedish / Nordic phone numbers with optional +46/+45/etc prefix
// - Swedish personal identity numbers (YYMMDD-XXXX / YYYYMMDD-XXXX)
// - Swedish organisation numbers (XXXXXX-XXXX / XXXXXXXXXX)
// - IPv4 addresses (could expose office networks)
// - Swedish vehicle reg numbers are NOT PII per se — we leave them as-is.
// ---------------------------------------------------------------------------
const EMAIL_RE = /[\w.+-]+@[\w-]+\.[\w.-]+/g;
const PHONE_RE = /(\+?(?:46|45|47|358|354)[\d\s().-]{6,}\d|\+?\d[\d\s().-]{7,}\d)/g;
const SSN_RE = /\b\d{6,8}-?\d{4}\b/g;        // personnummer + orgnr overlap
const IPV4_RE = /\b(?:\d{1,3}\.){3}\d{1,3}\b/g;

const scrubString = (s: string): string =>
  s
    .replace(EMAIL_RE, "[email]")
    .replace(SSN_RE, "[id-number]")
    .replace(PHONE_RE, "[phone]")
    .replace(IPV4_RE, "[ip]");

// Recursively walk any JSON-like value and scrub every string node.
const deepScrub = (value: unknown): unknown => {
  if (typeof value === "string") return scrubString(value);
  if (Array.isArray(value)) return value.map(deepScrub);
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      // Drop known PII keys entirely rather than just scrubbing values,
      // because Sentry's built-in scrubber may still index the key name.
      const lower = k.toLowerCase();
      if (
        lower === "email" ||
        lower === "e-mail" ||
        lower === "phone" ||
        lower === "phone_number" ||
        lower === "phonenumber" ||
        lower === "mobile" ||
        lower === "ssn" ||
        lower === "personnummer" ||
        lower === "orgnr" ||
        lower === "org_number" ||
        lower === "organisation_number" ||
        lower === "address" ||
        lower === "street_address" ||
        lower === "ip" ||
        lower === "ip_address" ||
        lower === "user_ip" ||
        lower === "cookie" ||
        lower === "authorization" ||
        lower === "x-auth-token" ||
        lower === "password" ||
        lower === "secret" ||
        lower === "token" ||
        lower === "api_key" ||
        lower === "apikey" ||
        lower === "api_secret"
      ) {
        out[k] = "[redacted]";
        continue;
      }
      out[k] = deepScrub(v);
    }
    return out;
  }
  return value;
};

// Strip query parameters from a URL that might contain PII (e.g. ?email=…).
const scrubUrl = (url: string | undefined): string | undefined => {
  if (!url) return url;
  try {
    const u = new URL(url, window.location.origin);
    const piiKeys = ["email", "e-mail", "phone", "token", "code", "invite", "auth"];
    let changed = false;
    for (const key of piiKeys) {
      if (u.searchParams.has(key)) {
        u.searchParams.set(key, "[redacted]");
        changed = true;
      }
    }
    // Also scrub any value that looks like an e-mail in query params.
    for (const [k, v] of u.searchParams.entries()) {
      if (EMAIL_RE.test(v)) {
        u.searchParams.set(k, scrubString(v));
        changed = true;
      }
    }
    return changed ? u.toString() : url;
  } catch {
    return url;
  }
};

let initialized = false;

export const initSentry = () => {
  if (initialized) return;
  if (!isMonitoredHost()) return;
  initialized = true;

  Sentry.init({
    dsn: DSN,
    environment: window.location.hostname.includes("lovable.app") ? "preview" : "production",
    release: typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : undefined,
    sampleRate: 1.0,
    tracesSampleRate: 0.05,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: true, blockAllMedia: true }),
    ],
    sendDefaultPii: false,
    beforeSend(event) {
      try {
        // 1. Message & exception
        if (event.message) event.message = scrubString(event.message);
        if (event.exception?.values) {
          for (const ex of event.exception.values) {
            if (ex.value) ex.value = scrubString(ex.value);
          }
        }

        // 2. Extra & contexts — deep recursive scrub
        if (event.extra) event.extra = deepScrub(event.extra) as Record<string, unknown>;
        if (event.contexts) event.contexts = deepScrub(event.contexts) as any;

        // 3. Request (URL, headers, body)
        if (event.request) {
          const req = event.request as Record<string, unknown>;
          if (typeof req.url === "string") req.url = scrubUrl(req.url);
          if (req.headers) req.headers = deepScrub(req.headers) as Record<string, string>;
          if (req.data) req.data = deepScrub(req.data);
          if (req.query_string) req.query_string = deepScrub(req.query_string);
          if (req.cookies) req.cookies = "[redacted]";
        }

        // 4. Breadcrumbs
        if (event.breadcrumbs) {
          event.breadcrumbs = event.breadcrumbs.map((b) => ({
            ...b,
            message: b.message ? scrubString(b.message) : b.message,
            data: b.data ? (deepScrub(b.data) as Record<string, unknown>) : b.data,
          }));
        }

        // 5. Tags — scrub any tag that happens to contain an e-mail or phone number
        if (event.tags) {
          event.tags = Object.fromEntries(
            Object.entries(event.tags).map(([k, v]) => [
              k,
              typeof v === "string" ? scrubString(v) : v,
            ])
          );
        }

        // 6. User — we only ever send `id`, but guard against future changes
        if (event.user) {
          const user = { ...event.user };
          if (user.email) user.email = "[redacted]";
          if (user.ip_address) user.ip_address = "[ip]";
          if (user.username) user.username = scrubString(user.username as string);
          if (user.name) user.name = scrubString(user.name as string);
          event.user = user;
        }
      } catch {
        /* never let a scrubber crash the reporter */
      }
      return event;
    },
  });
};

export const setSentryUser = (userId: string | null) => {
  if (!initialized) return;
  if (userId) Sentry.setUser({ id: userId });
  else Sentry.setUser(null);
};

export const setSentryCompany = (companyId: string | null) => {
  if (!initialized) return;
  Sentry.setTag("company_id", companyId ?? "none");
};

export const setSentryRoles = (roles: string[]) => {
  if (!initialized) return;
  Sentry.setTag("user_role", roles[0] ?? "none");
  Sentry.setTag("user_roles", roles.join(",") || "none");
};

export { Sentry };
